import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useConversation } from '@elevenlabs/react';

import strings from '../../localization';

import {
  isRecoverableElevenLabsClientToolError,
  isUnrecoverableElevenLabsDisconnect,
} from '../../components/SignupFlow/AiCompanion/elevenLabsClientTools';
import useAiInactivityFallback from './useAiInactivityFallback';
import { WorkflowDynamicVariables } from '../../components/SignupFlow/AiCompanion/types';
import { ConversationTranscript } from './useConversationTranscript';

interface IUseAiConversationSession {
  getDynamicVariables: () => Promise<WorkflowDynamicVariables>;
  isInteractionPaused: boolean;
  isVoiceMode: boolean;
  onBack: () => void;
  onConversationConnected: (conversationId: string) => void;
  onErrorChange: (message: string) => void;
  onFallbackToManual: () => void;
  onVoiceModeChange: (isVoiceMode: boolean) => void;
  signupRequestId: string;
  transcript: ConversationTranscript;
}

const getAgentLanguage = (preferredLanguage: string) => {
  const normalizedLanguage = preferredLanguage.trim().toLowerCase();
  return normalizedLanguage.startsWith('fr') ? 'fr' as const : 'en' as const;
};

const useAiConversationSession = ({
  getDynamicVariables,
  isInteractionPaused,
  isVoiceMode,
  onBack,
  onConversationConnected,
  onErrorChange,
  onFallbackToManual,
  onVoiceModeChange,
  signupRequestId,
  transcript,
}: IUseAiConversationSession) => {
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [hasRecoverableToolFailure, setHasRecoverableToolFailure] = useState(false);
  const [userActivitySequence, setUserActivitySequence] = useState(0);
  const sessionStartAttemptRef = useRef(0);
  const isDisposedRef = useRef(false);
  const canSendMessageRef = useRef(false);
  const autoStartedRef = useRef(false);
  const hasTriggeredManualFallbackRef = useRef(false);
  const {
    appendUserMessage,
    beginSession,
    discardPendingUserMessage,
    handleAgentChatResponsePart,
    handleMessage,
    queuePendingUserMessage,
  } = transcript;

  const handleUnrecoverableError = useCallback(() => {
    if (isDisposedRef.current || hasTriggeredManualFallbackRef.current) {
      return;
    }

    hasTriggeredManualFallbackRef.current = true;
    isDisposedRef.current = true;
    sessionStartAttemptRef.current += 1;
    onVoiceModeChange(false);
    onFallbackToManual();
  }, [onFallbackToManual, onVoiceModeChange]);

  const handleConversationError = useCallback((
    message: string,
    context?: unknown,
  ) => {
    setIsStartingSession(false);

    if (isRecoverableElevenLabsClientToolError(context)) {
      setHasRecoverableToolFailure(true);
      onErrorChange('');
      return;
    }

    onVoiceModeChange(false);
    onErrorChange(message || strings.aiCompanionConnectionError);
    handleUnrecoverableError();
  }, [handleUnrecoverableError, onErrorChange, onVoiceModeChange]);

  const handleConversationDisconnect = useCallback((...args: unknown[]) => {
    setHasRecoverableToolFailure(false);

    if (isUnrecoverableElevenLabsDisconnect(args[0])) {
      handleUnrecoverableError();
    }
  }, [handleUnrecoverableError]);

  const conversation = useConversation({
    onDisconnect: handleConversationDisconnect,
    onMessage: handleMessage,
    onAgentChatResponsePart: handleAgentChatResponsePart,
    onError: handleConversationError,
  });

  const {
    endSession,
    getInputByteFrequencyData,
    isSpeaking,
    sendUserActivity,
    sendUserMessage,
    setMuted,
    setVolume,
    startSession,
    status,
  } = conversation;

  const isConnected = status === 'connected' || hasRecoverableToolFailure;
  const isTransitioning = isStartingSession || status === 'connecting';
  const effectiveStatus = hasRecoverableToolFailure ? 'connected' : status;
  canSendMessageRef.current = isConnected && !isTransitioning;
  const latestTranscriptMessage = transcript.messages[
    transcript.messages.length - 1
  ] ?? null;
  const latestAgentResponseId = latestTranscriptMessage?.role === 'assistant'
    && !latestTranscriptMessage.isStreaming
    ? latestTranscriptMessage.id
    : null;
  const isWaitingForUserResponse = Boolean(
    isConnected
    && !isSpeaking
    && latestAgentResponseId,
  );

  const applyVoiceMode = useCallback((voiceModeEnabled: boolean) => {
    if (voiceModeEnabled) {
      setVolume({ volume: 1 });
      setMuted(false);
      return;
    }

    setMuted(true);
    setVolume({ volume: 0 });
  }, [setMuted, setVolume]);

  const startConversationSession = useCallback(async () => {
    const agentId = process.env.REACT_APP_ELEVEN_LABS_AGENT_ID;

    if (!agentId) {
      handleUnrecoverableError();
      return false;
    }

    const startAttempt = sessionStartAttemptRef.current + 1;
    sessionStartAttemptRef.current = startAttempt;

    try {
      setIsStartingSession(true);
      setHasRecoverableToolFailure(false);
      onErrorChange('');
      const dynamicVariables = await getDynamicVariables();

      if (
        isDisposedRef.current
        || sessionStartAttemptRef.current !== startAttempt
      ) {
        return false;
      }

      beginSession();
      startSession({
        agentId,
        dynamicVariables,
        connectionType: 'websocket',
        textOnly: false,
        userId: signupRequestId,
        onConnect: ({ conversationId }) => {
          onConversationConnected(conversationId);
        },
        onConversationCreated: (createdConversation) => {
          createdConversation.setVolume({ volume: 1 });
          createdConversation.setMicMuted(false);
        },
        overrides: {
          agent: {
            language: getAgentLanguage(dynamicVariables.preferred_language),
          },
        },
      });
      return true;
    } catch {
      const isCurrentAttempt = !isDisposedRef.current
        && sessionStartAttemptRef.current === startAttempt;

      if (isCurrentAttempt) {
        setIsStartingSession(false);
        handleUnrecoverableError();
      }
      return false;
    }
  }, [
    getDynamicVariables,
    beginSession,
    onConversationConnected,
    handleUnrecoverableError,
    onErrorChange,
    signupRequestId,
    startSession,
  ]);

  const sendMessage = useCallback((value: string) => {
    const message = value.trim();

    if (!message || !canSendMessageRef.current) {
      if (message) {
        onErrorChange(strings.aiCompanionConnectionError);
      }
      return false;
    }

    queuePendingUserMessage(message);

    try {
      sendUserMessage(message);
    } catch {
      discardPendingUserMessage(message);
      handleUnrecoverableError();
      return false;
    }

    onErrorChange('');
    appendUserMessage(message);
    return true;
  }, [
    appendUserMessage,
    discardPendingUserMessage,
    handleUnrecoverableError,
    onErrorChange,
    queuePendingUserMessage,
    sendUserMessage,
  ]);

  const handleUserActivity = useCallback(() => {
    setUserActivitySequence((currentSequence) =>
      currentSequence + 1);
    sendUserActivity();
  }, [sendUserActivity]);

  useAiInactivityFallback({
    activitySequence: userActivitySequence,
    isPaused: isInteractionPaused,
    isWaitingForUserResponse,
    latestAgentResponseId,
    onTimeout: handleUnrecoverableError,
  });

  const handleBack = useCallback(() => {
    isDisposedRef.current = true;
    sessionStartAttemptRef.current += 1;
    onVoiceModeChange(false);

    if (isConnected || isTransitioning) {
      endSession();
    }
    onBack();
  }, [
    endSession,
    isConnected,
    isTransitioning,
    onBack,
    onVoiceModeChange,
  ]);

  const handleModeChange = useCallback((voiceModeEnabled: boolean) => {
    if (!isConnected) {
      return;
    }

    try {
      applyVoiceMode(voiceModeEnabled);
      onErrorChange('');
    } catch {
      try {
        applyVoiceMode(false);
      } catch {
        // The conversation may have disconnected during the mode change.
      }
      onVoiceModeChange(false);
      onErrorChange(strings.voiceInputError);
    }
  }, [
    applyVoiceMode,
    isConnected,
    onErrorChange,
    onVoiceModeChange,
  ]);

  const handleTextModeButton = useCallback(() => {
    handleModeChange(false);
  }, [handleModeChange]);

  const handleVoiceButton = useCallback(() => {
    handleModeChange(true);
  }, [handleModeChange]);

  useEffect(() => {
    if (isDisposedRef.current) {
      autoStartedRef.current = false;
    }
    isDisposedRef.current = false;

    return () => {
      isDisposedRef.current = true;
      sessionStartAttemptRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (
      !autoStartedRef.current
      && ['disconnected', 'error'].includes(status)
    ) {
      autoStartedRef.current = true;
      startConversationSession();
    }
  }, [startConversationSession, status]);

  useEffect(() => {
    if (isConnected) {
      setIsStartingSession(false);
    }
  }, [isConnected]);

  useEffect(() => {
    if (isVoiceMode && !isConnected) {
      return;
    }

    try {
      applyVoiceMode(isVoiceMode);
    } catch {
      if (isVoiceMode) {
        onVoiceModeChange(false);
        onErrorChange(strings.voiceInputError);
      }
    }
  }, [
    applyVoiceMode,
    isConnected,
    isVoiceMode,
    onErrorChange,
    onVoiceModeChange,
  ]);

  return {
    getInputByteFrequencyData,
    handleBack,
    handleTextModeButton,
    handleVoiceButton,
    isConnected,
    isSpeaking,
    isTransitioning,
    sendMessage,
    sendUserActivity: handleUserActivity,
    status: effectiveStatus,
  };
};

export default useAiConversationSession;
